import { useEffect, useState } from "react";
import { Heart, Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useStore } from "@/lib/store";
import { useXC } from "@/lib/hooks";
import { vodSources } from "@/lib/xc";
import Player from "@/components/Player";
import NextUp from "@/components/NextUp";

export default function SeriesModal({ series, onClose }) {
  const { active, isFav, toggleFav, pushRecent, resumeAt, pct, setProgress, progress } = useStore();
  const { data, loading, error } = useXC(active, "get_series_info", { series_id: series?.id }, !!series);
  const [season, setSeason] = useState(null);
  const [ep, setEp] = useState(null);
  const [upNext, setUpNext] = useState(null);
  const episodes = data?.episodes || {};
  const seasons = Object.keys(episodes).sort((a, b) => Number(a) - Number(b));
  useEffect(() => {
    setEp(null);
    setSeason(null);
  }, [series?.id]);
  if (!series) return null;
  const cur = season || seasons[0];
  const info = data?.info || {};
  const backdrop = (Array.isArray(info.backdrop_path) ? info.backdrop_path[0] : info.backdrop_path) || series.icon;
  const fav = isFav("series", series.id);

  const skey = `series:${series.id}`;
  const last = progress[skey];
  const lastEp = last && Object.values(episodes).flat().find((e) => String(e.id) === String(last.epId));
  const ordered = seasons.flatMap((s) => (episodes[s] || []).map((e) => ({ ...e, seasonKey: s })));
  const play = (e) => {
    const found = ordered.find((x) => String(x.id) === String(e.id));
    if (found) setSeason(found.seasonKey);
    setUpNext(null);
    setEp({ ...e, startAt: resumeAt(`episode:${e.id}`) });
    pushRecent({ ...series, sub: e.title });
  };
  const onEnded = () => {
    const i = ordered.findIndex((x) => String(x.id) === String(ep.id));
    if (i >= 0 && i + 1 < ordered.length) setUpNext(ordered[i + 1]);
  };
  const onProgress = (pos, dur) =>
    setProgress({ [`episode:${ep.id}`]: { pos, dur }, [skey]: { pos, dur, epId: ep.id, epTitle: ep.title } });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0D14] border-[#212738] text-slate-100 max-w-5xl p-0 overflow-hidden max-h-[92vh] flex flex-col [&>button]:hidden" data-testid="series-modal">
        <DialogTitle className="sr-only">{series.name}</DialogTitle>
        <div className="absolute top-3 right-3 z-20">
          <button onClick={onClose} className="p-2 rounded-full bg-black/60 hover:bg-black" data-testid="series-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="aspect-[21/9] relative bg-black shrink-0">
          {ep ? (
            <>
              <Player key={ep.id} sources={vodSources(active, "series", ep.id, ep.container_extension)} startAt={ep.startAt} onProgress={onProgress} onEnded={onEnded} />
              {upNext && <NextUp episode={upNext} onPlay={() => play(upNext)} onCancel={() => setUpNext(null)} />}
            </>
          ) : (
            <>
              {backdrop && <img src={backdrop} alt="" className="w-full h-full object-cover opacity-50" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D14] to-transparent" />
              <div className="absolute bottom-0 p-8 max-w-3xl">
                <h2 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="series-title">{info.name || series.name}</h2>
                <p className="text-sm text-slate-400 mt-2 line-clamp-2">{info.plot}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {lastEp && (
                    <button onClick={() => play(lastEp)} className="xc-btn-primary" data-testid="series-resume-button">
                      <Play className="w-4 h-4 fill-white" /> {resumeAt(`episode:${lastEp.id}`) ? "Resume" : "Replay"} {lastEp.title}
                    </button>
                  )}
                  <button onClick={() => toggleFav(series)} className="xc-btn-ghost" data-testid="series-fav-button">
                    <Heart className={`w-4 h-4 ${fav ? "fill-[#E50914] text-[#E50914]" : ""}`} /> {fav ? "In favourites" : "Add to favourites"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        {ep && <div className="px-8 pt-3 text-sm text-slate-300" data-testid="series-now-playing">Now playing: {ep.title}</div>}
        <div className="px-8 pt-4 flex gap-2 overflow-x-auto shrink-0 xc-scroll">
          {seasons.map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm border transition-colors ${s === cur ? "bg-[#E50914] border-[#E50914] text-white" : "border-[#212738] text-slate-400 hover:text-white"}`}
              data-testid={`season-tab-${s}`}
            >
              Season {s}
            </button>
          ))}
        </div>
        <div className="p-8 pt-4 overflow-y-auto xc-scroll space-y-2">
          {loading && <div className="text-sm text-slate-500">Loading episodes…</div>}
          {error && <div className="text-sm text-[#FF5A62]">{error}</div>}
          {(episodes[cur] || []).map((e) => (
            <button
              key={e.id}
              onClick={() => play(e)}
              className={`w-full flex items-center gap-4 rounded-xl border p-3 text-left transition-colors ${ep?.id === e.id ? "border-[#E50914]/60 bg-[#E50914]/5" : "border-[#212738] hover:border-[#38425C] hover:bg-[#11141F]"}`}
              data-testid={`episode-${e.id}`}
            >
              <div className="w-32 aspect-video rounded-lg overflow-hidden bg-[#1A1E2E] shrink-0 relative">
                {e.info?.movie_image && <img src={e.info.movie_image} alt="" loading="lazy" className="w-full h-full object-cover" onError={(x) => (x.currentTarget.style.display = "none")} />}
                <Play className="absolute inset-0 m-auto w-6 h-6 fill-white/90 text-white/90" />
                {pct(`episode:${e.id}`) > 0 && (
                  <div className="absolute bottom-0 inset-x-0 h-1 bg-black/60" data-testid={`episode-progress-${e.id}`}>
                    <div className="h-full bg-[#E50914]" style={{ width: `${pct(`episode:${e.id}`) * 100}%` }} />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{e.episode_num}. {e.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2 mt-1">{e.info?.plot}</div>
                {e.info?.duration && <div className="text-[11px] font-mono text-slate-500 mt-1">{e.info.duration}</div>}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
